import { client } from '@/api/orpc'
import { useIterator } from '@/composables/useIterator'
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useUser } from './user'
import { useRoute } from 'vue-router'

export const useBattles = defineStore('battles', () => {
	const list = ref<
		{
			uuid: string
			status: 'running' | 'finished' | 'created' | 'waiting_for_block'
			participants: {
				name: string | null
				avatar: string | null
				isBot: boolean | null
				teamIndex: number
				slotIndex: number
				winner: {
					wonCoins: number
				} | null
				me: boolean | null
			}[]
			cases: string[]
			entryFee: number
			gameMode: 'classic' | 'shared' | 'jackpot'
			modifiers: ('crazy' | 'fast')[]
			teamIndexWinner: number | null
			tieBreakWinner: number | null
			ts: number | null
			funding: number
		}[]
	>([])

	const gamemode = ref<'classic' | 'shared' | 'jackpot'>('classic')

	const setGamemode = (mode: 'classic' | 'shared' | 'jackpot') => {
		gamemode.value = mode

		if (gamemode.value === 'shared' && selectedPlayers.value === 1) {
			selectedPlayers.value = 0
		}
	}

	const selectedPlayers = ref(0)

	const selectedCases = ref<string[]>([])

	const addCaseToSelection = (uuid: string) => {
		selectedCases.value.push(uuid)
	}

	const removeCaseFromSelection = (uuid: string) => {
		const index = selectedCases.value.indexOf(uuid)
		if (index === -1) {
			return
		}

		selectedCases.value.splice(index, 1)
	}

	const resetCases = () => {
		selectedCases.value = []
	}

	const privateGame = ref(false)
	const crazy = ref(false)
	const fast = ref(false)
	const customFunding = ref(false)
	const fundingPercent = ref(50)
	const emptySlot = ref(false)

	const setBattleParams = (
		cases: string[],
		mode: 'classic' | 'shared' | 'jackpot',
		isPrivate: boolean,
		modifiers: ('crazy' | 'fast')[],
		players: number
	) => {
		selectedCases.value = cases
		gamemode.value = mode
		privateGame.value = isPrivate
		crazy.value = modifiers.includes('crazy')
		selectedPlayers.value = players
	}

	const reset = () => {
		gamemode.value = 'classic'
		selectedPlayers.value = 0
		resetCases()
		privateGame.value = false
		crazy.value = false
		fast.value = false
		customFunding.value = false
		fundingPercent.value = 50
	}

	const user = useUser()

	const calcSettleTimeMs = (
		startedAt: number | null,
		fast: boolean,
		rounds: number,
		participants: number,
		tieBreakWinner: number | null
	) => {
		if (!startedAt) {
			return 0
		}

		let totalDuration = 0

		for (let i = 0; i < rounds; i++) {
			let roundDuration = 0

			if (fast) {
				roundDuration = 3210 + participants * 100
			} else {
				roundDuration = 5210 + participants * 300
			}

			totalDuration += roundDuration
		}

		totalDuration += fast ? 3000 : 6000 + participants * 600 + 2000

		if (tieBreakWinner !== null) {
			totalDuration += 3650
		}

		return startedAt + totalDuration
	}

	const checkIfEnded = (
		startedAt: number | null,
		fast: boolean,
		rounds: number,
		participants: number,
		tieBreakWinner: number | null
	) => {
		if (!startedAt) {
			return false
		}

		const diff = startedAt ? user.now() - startedAt : 0
		const totalDuration =
			calcSettleTimeMs(startedAt, fast, rounds, participants, tieBreakWinner) -
			startedAt

		if (totalDuration > diff) {
			return false
		}

		return true
	}

	const route = useRoute()

	const loading = ref(true)

	const subToUpdates = async () => {
		loading.value = true

		const { data: iterator, error } = await client.battle.subBattlesUpdates(
			{},
			{
				context: {
					retry: Number.POSITIVE_INFINITY,
					onRetry: () => {
						loading.value = true

						return (isSuccess) => {
							if (!isSuccess) {
								return
							}

							loading.value = false
						}
					}
				}
			}
		)

		if (error) {
			loading.value = false
			return console.error(error)
		}

		if (!iterator) {
			loading.value = false
			return
		}

		try {
			for await (const events of iterator) {
				await user.nowAvailable()

				if (!list.value.length) {
					list.value = events.map((event) => {
						if (event.status !== 'finished') {
							return event
						}

						return {
							...event,
							status: checkIfEnded(
								event.ts,
								event.modifiers.includes('fast'),
								event.cases.length,
								event.participants.length,
								event.tieBreakWinner
							)
								? 'finished'
								: 'running'
						}
					})

					user.pushUnsettledTransactions(getUnsettledUserBattles())
				} else {
					for (const event of events) {
						const battle = list.value.find((b) => b.uuid === event.uuid)

						if (battle) {
							if (event.status === 'finished') {
								event.status = 'running'
							}
							Object.assign(battle, event)
						} else {
							list.value.push(event)
						}

						if (
							event.participants.some((p) => p.me) &&
							event.status === 'waiting_for_block' &&
							!route.path.startsWith('/battle/')
						) {
							user.battleStarted(event.uuid)
						}
					}
				}

				user.setDisplayCoins(true)
				loading.value = false
			}
		} catch (error: unknown) {
			console.error('ERROR IN BATTLES SUB', error)
		}
	}

	const setStatus = (
		uuid: string,
		status: 'running' | 'finished' | 'created' | 'waiting_for_block'
	) => {
		const battle = list.value.find((b) => b.uuid === uuid)

		if (!battle) {
			return
		}

		battle.status = status
	}

	const getUnsettledUserBattles = () =>
		list.value
			.filter(
				(b) =>
					b.status === 'running' &&
					b.participants.some((p) => p.me && p.winner && p.winner.wonCoins)
			)
			.map((b) => {
				const participant = b.participants.find((p) => p.me)
				if (!participant) {
					return null
				}

				return {
					uuid: b.uuid,
					coins: participant.winner ? participant.winner.wonCoins : 0,
					settledAt: calcSettleTimeMs(
						b.ts,
						b.modifiers.includes('fast'),
						b.cases.length,
						b.participants.length,
						b.tieBreakWinner
					)
				}
			})
			.filter((b) => b !== null)

	const toggleEmptySlot = () => {
		emptySlot.value = !emptySlot.value
	}

	return {
		list,
		loading,
		setStatus,
		gamemode,
		setGamemode,
		selectedPlayers,
		selectedCases,
		addCaseToSelection,
		removeCaseFromSelection,
		privateGame,
		crazy,
		fast,
		customFunding,
		fundingPercent,
		emptySlot,
		toggleEmptySlot,
		setBattleParams,
		reset,
		resetCases,
		getUnsettledUserBattles,
		subToUpdates
	}
})
